/* eslint-disable react-hooks/exhaustive-deps */
import classes from './Engagements.module.scss';
import EngagementFilters from './EngagementFilters';
import { Suspense, lazy, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Fade } from 'react-awesome-reveal';
import uuid from 'react-native-uuid';
import { useQuery } from '@tanstack/react-query';

import EngagementPill from './EngagementPill';
import { exportData } from 'store/customer/customer-view-actions';

import {
  REPLACE_STRING,
  ENGAGEMENT_EXPRESSIONS,
  TABLE_NAME_ENGAGEMENT,
  VIEW_FILTER_DATE_FORMAT,
  QUERY_KEY,
  COMPONENT_MAPPING,
} from 'constants/constants';
import { getEngagementTimelineAndPills } from 'store/customer/timeline/timeline-view-actions';
import { buildEngagementExpression } from 'utils/utils';
import { Spinner } from 'components/shared/loader/Spinner';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import { Tooltip } from 'react-bootstrap';
import { resetEngagementFilters } from 'store/customer/customerViewSlice';
import OverlayIcon from 'components/shared/overlay-icon/OverlayIcon';
import moment from 'moment';
import {
  setMetrics,
  setTimeline,
} from 'store/customer/timeline/timeline-view-slice';

const Timeline = lazy(() => import('../engagements/Timeline'));

const Engagements = () => {
  const dispatch = useDispatch();

  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );
  const selectedEcosystem = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.EngagementEcosystem
  );
  const selectedStakeholderName = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.StakeholderName
  );
  const selectedStakeholderType = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.StakeholderType
  );
  const selectedBrand = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.Brand
  );
  const selectedIndication = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.Indication
  );
  const selectedUser = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.GNE_User_Name
  );
  const selectedRole = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.GENE_User_Role
  );
  const selectedSquad = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.Squad
  );
  const selectedEngagements = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.Engagement
  );
  const selectedStartDate = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.startDate?.selectedValue
  );

  const selectedEndDate = useSelector(
    (state) => state?.CustomerView?.engagementFilters?.endDate?.selectedValue
  );

  const pills = useSelector((state) => state?.TimelineView?.metrics);

  const activeSectionTab = useSelector(
    (state) => state?.CustomerView?.activeSectionTab
  );

  // Fetch timeline and pills data whenever filters change
  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.EngagementTimelinePill,
      selectedCustomer?.value,
      selectedEngagements,
      selectedBrand,
      selectedIndication,
      selectedStakeholderName,
      selectedStakeholderType,
      selectedSquad,
      selectedEcosystem,
      moment(selectedStartDate).format(VIEW_FILTER_DATE_FORMAT),
      moment(selectedEndDate).format(VIEW_FILTER_DATE_FORMAT),
      selectedUser,
      selectedRole,
    ],
    queryFn: async () => {
      // Build expression for API call
      const replaceWith = buildExpression();
      const expression = ENGAGEMENT_EXPRESSIONS.metrics.expression.replace(
        REPLACE_STRING,
        replaceWith
      );

      // Fetch timeline and pills data
      const result = await getEngagementTimelineAndPills(expression);

      return result;
    },
    enabled:
      activeSectionTab === COMPONENT_MAPPING.Engagements &&
      selectedStartDate &&
      selectedEndDate
        ? true
        : false,
  });

  // Update Redux store with fetched data
  useEffect(() => {
    if (data) {
      dispatch(setMetrics(data?.pills || []));
      dispatch(setTimeline(data?.timeline || []));
    }
  }, [data]);

  const buildExpression = () => {
    return buildEngagementExpression(
      selectedEngagements,
      selectedSquad,
      selectedStakeholderName,
      selectedStakeholderType,
      selectedBrand,
      selectedIndication,
      selectedStartDate,
      selectedEndDate,
      selectedCustomer,
      selectedUser,
      selectedRole,
      selectedEcosystem
    );
  };

  const downloadData = () => {
    dispatch(
      exportData(
        TABLE_NAME_ENGAGEMENT,
        undefined,
        ENGAGEMENT_EXPRESSIONS.details.expression.replace(
          REPLACE_STRING,
          buildExpression()
        )
      )
    );
  };

  const exportTooltip = (
    <Tooltip className='gne-tooltip'>Send to Excel</Tooltip>
  );

  const resetFilters = () => {
    dispatch(resetEngagementFilters());
  };

  return (
    <div
      className={classes['engagements-container']}
      id='Engagements'>
      {/* title */}
      <div className='d-flex justify-content-between align-items-center'>
        <span className='title title__bold title__large'> Engagements </span>
        <div className='d-flex justify-content-between align-items-center'>
          <OverlayIcon
            tooltip={'Reset Filters'}
            onClick={resetFilters}
            className={`icon icon__reset-filters `}
          />
          <OverlayTrigger overlay={exportTooltip}>
            <i
              onClick={downloadData}
              className={`icon icon__affiliations_export_excel1 ms-2`}
            />
          </OverlayTrigger>
        </div>
      </div>

      {/* filters */}
      <div className={classes['engagements-tab-container']}>
        <EngagementFilters />
      </div>

      <Fade>
        <div className='gne-pill'>
          {pills?.map((item) => (
            <EngagementPill
              item={item}
              key={uuid.v4()}
            />
          ))}
          <Spinner
            area={isLoading}
            css={'d-inline-block ms-2'}
          />
        </div>
      </Fade>

      {/* timeline and engagement table*/}
      <Suspense fallback={<Spinner />}>
        <Timeline />
      </Suspense>
    </div>
  );
};

export default Engagements;
